/**
 * PSP/CRE TOOL v2.1 - SURVIVAL CURVES MODULE
 * 
 * @fileoverview Time-dependent survival curves for trauma casualties
 * @module constants/survivalCurves
 * @author Dr. Tatar Radu MD - Romanian Military Medical Institute
 * @date December 22, 2024
 * @version 2.1 - icons.js Integration
 * 
 * DATA SOURCES:
 * - Ukraine War data 2022-2024 (validated survival rates)
 * - NATO Joint Trauma System
 * - Committee on Tactical Combat Casualty Care (CoTCCC)
 * - Dupuy Institute combat casualty research
 * - Israeli Defense Forces medical doctrine
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (STATUS_ICONS, TIME_ICONS, MEDICAL_ICONS)
 * - Added getTimeWindowOptions() for UI dropdowns
 * - Added getTriageCategoryOptions() for UI
 * - Added getMechanismOptions() for UI
 * - Added validateSurvivalConfig() function
 * - Enhanced educational metadata
 * 
 * CRITICAL SURVIVAL DATA (Ukraine 2022-2024 Validated):
 * - Platinum 10 (<10 min): 98% T1 survival with interventions
 * - Golden Hour (<60 min): 92% T1 survival
 * - 60-120 min: 76% T1 survival
 * - 120-180 min: 54% T1 survival
 * - 180-240 min: 32% T1 survival
 * - >240 min: 18% T1 survival
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  TIME_ICONS,
  MEDICAL_ICONS,
  TACTICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  BLUE: STATUS_ICONS?.BLUE || '🔵',
  
  // Time icons
  CLOCK: TIME_ICONS?.CLOCK || '⏱️',
  HOURGLASS: TIME_ICONS?.HOURGLASS || '⏳',
  STOPWATCH: TIME_ICONS?.STOPWATCH || '⏱️',
  
  // Medical icons
  MEDICAL: MEDICAL_ICONS?.CROSS || '➕',
  BLOOD: MEDICAL_ICONS?.BLOOD || '🩸',
  HOSPITAL: MEDICAL_ICONS?.HOSPITAL || '🏥',
  
  // Tactical icons
  WARNING: TACTICAL_ICONS?.WARNING || '⚠️',
  TARGET: TACTICAL_ICONS?.TARGET || '🎯'
};

// ============================================================================
// TIME WINDOW DEFINITIONS
// ============================================================================

/**
 * Granular survival time windows
 * More precise than simple golden hour dichotomy
 */
export const SURVIVAL_TIME_WINDOWS = {
  PLATINUM_5: {
    code: 'PLATINUM_5',
    range_minutes: [0, 5],
    label: 'Platinum 5',
    icon: ICONS.GREEN,
    color: '#4CAF50',
    description: 'Platinum 5 - Immediate intervention',
    t1_survival: 0.99,
    critical_interventions: ['Tourniquet', 'Direct pressure'],
    achievability: 0.25,
    educational: {
      key_point: 'Best possible outcome - 99% survival',
      teaching_note: 'Requires immediate buddy aid/self-aid'
    }
  },
  PLATINUM_10: {
    code: 'PLATINUM_10',
    range_minutes: [5, 10],
    label: 'Platinum 10',
    icon: ICONS.GREEN,
    color: '#66BB6A',
    description: 'Platinum 10 - Point of injury care',
    t1_survival: 0.97,
    // CALC-010: moved here from a literal inside survivalModule (value unchanged, 2026-10-09).
    // Survival bonus when first intervention happens inside the platinum period. Source still to be confirmed.
    survival_bonus: 0.15,
    critical_interventions: ['Airway', 'Needle decompression', 'Hemostatics'],
    achievability: 0.48,
    educational: {
      key_point: '97% survival - TCCC interventions critical',
      teaching_note: 'CLS arrival should be within this window'
    }
  },
  GOLDEN_30: {
    code: 'GOLDEN_30',
    range_minutes: [10, 30],
    label: 'Golden 30',
    icon: ICONS.GREEN,
    color: '#81C784',
    description: 'Golden 30 - Rapid evacuation',
    t1_survival: 0.94,
    critical_interventions: ['IV access', 'Pain management', 'Reassessment'],
    achievability: 0.55,
    educational: {
      key_point: '94% survival - evacuation should be underway',
      teaching_note: 'TXA must be given in this window for maximum effect'
    }
  },
  GOLDEN_60: {
    code: 'GOLDEN_60',
    range_minutes: [30, 60],
    label: 'Golden Hour',
    icon: ICONS.GREEN,
    color: '#A5D6A7',
    description: 'Golden Hour - Surgery within 60 minutes',
    t1_survival: 0.92,
    critical_interventions: ['Surgical intervention', 'Blood products'],
    achievability: 0.38,
    educational: {
      key_point: 'NATO standard - 92% survival',
      teaching_note: 'Only 38% achieve this in Ukraine contested environment'
    }
  },
  EXTENDED_90: {
    code: 'EXTENDED_90',
    range_minutes: [60, 90],
    label: 'Extended 90',
    icon: ICONS.YELLOW,
    color: '#FFF176',
    description: 'Extended - 1 to 1.5 hours',
    t1_survival: 0.84,
    pfc_required: true,
    achievability: 0.60,
    educational: {
      key_point: '8% survival drop - PFC now critical',
      teaching_note: 'Common scenario in contested environment'
    }
  },
  EXTENDED_120: {
    code: 'EXTENDED_120',
    range_minutes: [90, 120],
    label: 'Extended 120',
    icon: ICONS.YELLOW,
    color: '#FFD54F',
    description: 'Extended - 1.5 to 2 hours',
    t1_survival: 0.76,
    pfc_required: true,
    achievability: 0.75,
    educational: {
      key_point: '16% survival drop from golden hour',
      teaching_note: 'PFC skills essential for medics'
    }
  },
  PROLONGED_180: {
    code: 'PROLONGED_180',
    range_minutes: [120, 180],
    label: 'Prolonged 180',
    icon: ICONS.ORANGE,
    color: '#FFB74D',
    description: 'Prolonged - 2 to 3 hours',
    t1_survival: 0.54,
    pfc_required: true,
    pfc_critical: true,
    achievability: 0.85,
    educational: {
      key_point: 'Nearly half will die',
      teaching_note: 'This is common reality in denied environment'
    }
  },
  PROLONGED_240: {
    code: 'PROLONGED_240',
    range_minutes: [180, 240],
    label: 'Prolonged 240',
    icon: ICONS.ORANGE,
    color: '#FF8A65',
    description: 'Prolonged - 3 to 4 hours',
    t1_survival: 0.32,
    pfc_required: true,
    pfc_critical: true,
    achievability: 0.92,
    educational: {
      key_point: 'Two-thirds will die',
      teaching_note: 'Consider expectant triage for most severe'
    }
  },
  CRITICAL_360: {
    code: 'CRITICAL_360',
    range_minutes: [240, 360],
    label: 'Critical',
    icon: ICONS.RED,
    color: '#E57373',
    description: 'Critical - 4 to 6 hours',
    t1_survival: 0.22,
    pfc_required: true,
    dcs_may_be_required: true,
    achievability: 0.95,
    educational: {
      key_point: 'Medical system overwhelmed',
      teaching_note: 'Forward surgical capability essential'
    }
  },
  CATASTROPHIC: {
    code: 'CATASTROPHIC',
    range_minutes: [360, Infinity],
    label: 'Catastrophic',
    icon: ICONS.RED,
    color: '#EF5350',
    description: 'Catastrophic - >6 hours',
    t1_survival: 0.15,
    pfc_required: true,
    dcs_required: true,
    achievability: 1.0,
    educational: {
      key_point: 'Only 15% survive',
      teaching_note: 'Mass expectant triage may be necessary'
    }
  }
};

// ============================================================================
// TRIAGE CATEGORY SURVIVAL CURVES
// ============================================================================

/**
 * T1 (IMMEDIATE) - Time-critical survival curve
 */
export const T1_SURVIVAL_CURVE = {
  category: 'T1',
  name: 'IMMEDIATE',
  icon: ICONS.RED,
  color: '#F44336',
  description: 'Life-threatening injuries requiring immediate intervention',
  
  // Granular survival by time (minutes)
  survival_by_minutes: {
    0: 1.00, 5: 0.99, 10: 0.97, 15: 0.96, 20: 0.95,
    30: 0.94, 45: 0.93, 60: 0.92, 75: 0.88, 90: 0.84,
    105: 0.80, 120: 0.76, 150: 0.65, 180: 0.54,
    210: 0.43, 240: 0.32, 300: 0.25, 360: 0.22,
    480: 0.18, 600: 0.15
  },
  
  // Survival windows summary
  survival_windows: {
    under_10_min: { rate: 0.97, icon: ICONS.GREEN },
    under_30_min: { rate: 0.94, icon: ICONS.GREEN },
    under_60_min: { rate: 0.92, icon: ICONS.GREEN },
    '60_to_90_min': { rate: 0.84, icon: ICONS.YELLOW },
    '90_to_120_min': { rate: 0.76, icon: ICONS.YELLOW },
    '120_to_180_min': { rate: 0.54, icon: ICONS.ORANGE },
    '180_to_240_min': { rate: 0.32, icon: ICONS.RED },
    over_240_min: { rate: 0.18, icon: ICONS.RED }
  },
  
  // Decay parameters for continuous function
  decay_parameters: {
    initial_survival: 1.0,
    golden_hour_survival: 0.92,
    half_life_minutes: 120,
    minimum_survival: 0.10
  },
  
  // Injury types
  injury_types: [
    'Thoracic hemorrhage',
    'Abdominal hemorrhage',
    'Major vascular injury',
    'Tension pneumothorax',
    'Traumatic brain injury requiring decompression',
    'Airway compromise'
  ],
  
  educational: {
    key_point: 'Time is life - every minute counts',
    teaching_note: 'These patients have survivable injuries IF treated in time'
  }
};

/**
 * T2 (DELAYED) - Stable but requires surgery
 */
export const T2_SURVIVAL_CURVE = {
  category: 'T2',
  name: 'DELAYED',
  icon: ICONS.YELLOW,
  color: '#FFC107',
  description: 'Serious injuries, can tolerate delay to surgery',
  
  survival_by_hours: {
    0: 1.00, 1: 0.99, 2: 0.98, 4: 0.97, 6: 0.96,
    12: 0.94, 24: 0.91, 48: 0.89, 72: 0.87, 96: 0.85, 120: 0.82
  },
  
  survival_windows: {
    under_2_hours: { rate: 0.98, icon: ICONS.GREEN },
    under_6_hours: { rate: 0.96, icon: ICONS.GREEN },
    '6_to_12_hours': { rate: 0.94, icon: ICONS.GREEN },
    '12_to_24_hours': { rate: 0.91, icon: ICONS.YELLOW },
    '24_to_72_hours': { rate: 0.87, icon: ICONS.YELLOW },
    over_72_hours: { rate: 0.82, icon: ICONS.ORANGE }
  },
  
  decay_parameters: {
    initial_survival: 1.0,
    plateau_survival: 0.96,
    plateau_duration_hours: 6,
    decay_rate_per_hour: 0.002,
    minimum_survival: 0.75
  },
  
  injury_types: [
    'Stable extremity wounds',
    'Facial trauma (airway secure)',
    'Closed fractures',
    'Moderate burns',
    'Soft tissue injuries'
  ],
  
  educational: {
    key_point: 'Stable but serious - do not neglect',
    teaching_note: 'These patients can wait but should not wait forever'
  }
};

/**
 * T3 (MINIMAL) - Walking wounded
 */
export const T3_SURVIVAL_CURVE = {
  category: 'T3',
  name: 'MINIMAL',
  icon: ICONS.GREEN,
  color: '#4CAF50',
  description: 'Minor injuries, ambulatory',
  
  survival_by_hours: {
    0: 1.00, 24: 0.99, 48: 0.99, 72: 0.98, 96: 0.98, 168: 0.97
  },
  
  survival_windows: {
    under_24_hours: { rate: 0.99, icon: ICONS.GREEN },
    under_72_hours: { rate: 0.98, icon: ICONS.GREEN },
    over_72_hours: { rate: 0.97, icon: ICONS.GREEN }
  },
  
  decay_parameters: {
    initial_survival: 1.0,
    minimum_survival: 0.95,
    infection_risk_per_day: 0.002
  },
  
  injury_types: [
    'Superficial wounds',
    'Minor fractures',
    'Minor burns (<15% TBSA)',
    'Mild concussion',
    'Sprains'
  ],
  
  educational: {
    key_point: 'Walking wounded - can self-evacuate',
    teaching_note: 'May have significant psychological impact'
  }
};

/**
 * T4 (EXPECTANT) - Likely non-survivable
 */
export const T4_SURVIVAL_CURVE = {
  category: 'T4',
  name: 'EXPECTANT',
  icon: ICONS.RED,
  color: '#9E9E9E',
  description: 'Injuries incompatible with survival given available resources',
  
  survival_by_hours: {
    0: 0.15, 1: 0.10, 2: 0.08, 4: 0.05, 6: 0.03, 12: 0.02, 24: 0.01
  },
  
  survival_windows: {
    under_1_hour: { rate: 0.10, icon: ICONS.RED },
    under_6_hours: { rate: 0.03, icon: ICONS.RED },
    over_6_hours: { rate: 0.01, icon: ICONS.RED }
  },
  
  decay_parameters: {
    initial_survival: 0.15,
    half_life_hours: 2,
    minimum_survival: 0.01
  },
  
  injury_types: [
    'Severe TBI with herniation',
    'Burns >80% TBSA',
    'Multiple traumatic amputations with prolonged evacuation',
    'Massive trauma with failed resuscitation'
  ],
  
  notes: 'Category may change if resources become available',
  
  educational: {
    key_point: 'Expectant is not the same as dead',
    teaching_note: 'Provide comfort care, re-triage if resources allow'
  }
};

// ============================================================================
// PFC SURVIVAL MODIFIERS
// ============================================================================

/**
 * PFC (Prolonged Field Care) improves survival when evacuation delayed
 */
export const PFC_SURVIVAL_MODIFIERS = {
  NO_PFC: {
    code: 'NO_PFC',
    label: 'No PFC Available',
    icon: ICONS.RED,
    modifier: 0.70,
    applicable_after_minutes: 60,
    description: 'Without PFC, survival drops significantly after golden hour'
  },
  BASIC_PFC: {
    code: 'BASIC_PFC',
    label: 'Basic PFC',
    icon: ICONS.YELLOW,
    modifier: 0.90,
    applicable_after_minutes: 60,
    description: 'Basic monitoring, IV maintenance, reassessment',
    capabilities: ['Vital sign monitoring', 'IV maintenance', 'Wound reassessment']
  },
  INTERMEDIATE_PFC: {
    code: 'INTERMEDIATE_PFC',
    label: 'Intermediate PFC',
    icon: ICONS.GREEN,
    modifier: 0.95,
    applicable_after_minutes: 60,
    description: 'Blood products, advanced monitoring',
    capabilities: ['Blood products', 'Advanced airway', 'Chest tube management']
  },
  ADVANCED_PFC: {
    code: 'ADVANCED_PFC',
    label: 'Advanced PFC',
    icon: ICONS.GREEN,
    modifier: 0.98,
    applicable_after_minutes: 60,
    description: 'Near-surgical capability in field',
    capabilities: ['Damage control surgery', 'Anesthesia', 'Ventilator']
  },
  SUSTAINED_PFC: {
    code: 'SUSTAINED_PFC',
    label: 'Sustained PFC',
    icon: ICONS.GREEN,
    modifier: 1.0,
    applicable_after_minutes: 60,
    description: 'Full forward surgical team capability',
    capabilities: ['Complete DCS', 'ICU-level care', 'Specialist surgery']
  }
};

// ============================================================================
// MECHANISM SURVIVAL MODIFIERS
// ============================================================================

/**
 * Injury mechanism affects baseline survival
 */
export const MECHANISM_SURVIVAL_MODIFIERS = {
  ARTILLERY: {
    code: 'ARTILLERY',
    label: 'Artillery/Mortar',
    icon: ICONS.TARGET,
    base_survival_modifier: 0.85,
    injury_pattern: 'Multiple fragment wounds, blast, burns',
    time_sensitivity: 'HIGH',
    ukraine_data: {
      percentage_2024: 0.50,
      typical_injuries: ['Fragment wounds', 'Blast lung', 'TBI', 'Burns']
    },
    educational: {
      teaching_note: '50% of Ukraine casualties - expect multiple wounds'
    }
  },
  FPV_DRONE: {
    code: 'FPV_DRONE',
    label: 'FPV Drone',
    icon: ICONS.TARGET,
    base_survival_modifier: 0.75,
    injury_pattern: 'Concentrated blast, high head/neck injury rate',
    time_sensitivity: 'HIGH',
    ukraine_data: {
      percentage_2024: 0.25,
      head_neck_rate: 0.35,
      eye_injury_rate: 0.18
    },
    educational: {
      teaching_note: 'New threat - 35% head/neck injuries vs 20% for artillery'
    }
  },
  MINE_IED: {
    code: 'MINE_IED',
    label: 'Mine/IED',
    icon: ICONS.WARNING,
    base_survival_modifier: 0.70,
    injury_pattern: 'Traumatic amputation, pelvic fracture, massive hemorrhage',
    time_sensitivity: 'CRITICAL',
    ukraine_data: {
      percentage_2024: 0.12,
      amputation_rate: 0.45,
      pelvic_fracture_rate: 0.40
    },
    educational: {
      teaching_note: 'High tourniquet need, pelvic binder essential'
    }
  },
  SMALL_ARMS: {
    code: 'SMALL_ARMS',
    label: 'Small Arms',
    icon: ICONS.TARGET,
    base_survival_modifier: 0.88,
    injury_pattern: 'Penetrating wounds, usually single mechanism',
    time_sensitivity: 'MODERATE',
    ukraine_data: {
      percentage_2024: 0.08
    },
    educational: {
      teaching_note: 'Most survivable mechanism with proper care'
    }
  },
  VEHICLE: {
    code: 'VEHICLE',
    label: 'Vehicle Strike/Fire',
    icon: ICONS.WARNING,
    base_survival_modifier: 0.65,
    injury_pattern: 'Burns, crush injury, entrapment',
    time_sensitivity: 'CRITICAL',
    ukraine_data: {
      percentage_2024: 0.05,
      burn_rate: 0.60
    },
    educational: {
      teaching_note: 'Complex rescue, high burn rate'
    }
  },
  BLAST: {
    code: 'BLAST',
    label: 'Pure Blast',
    icon: ICONS.WARNING,
    base_survival_modifier: 0.80,
    injury_pattern: 'Blast lung, TBI, internal injuries',
    time_sensitivity: 'HIGH',
    educational: {
      teaching_note: 'May look stable but deteriorate rapidly'
    }
  }
};

// ============================================================================
// BODY REGION SURVIVAL MODIFIERS
// ============================================================================

/**
 * Body region affects survival rate
 */
export const BODY_REGION_SURVIVAL_MODIFIERS = {
  HEAD_NECK: {
    code: 'HEAD_NECK',
    label: 'Head/Neck',
    icon: ICONS.RED,
    survival_modifier: 0.65,
    requires_specialist: true,
    surgical_capability: 'Neurosurgery/ENT',
    educational: {
      teaching_note: 'FPV drones causing 35% head/neck injuries'
    }
  },
  THORAX: {
    code: 'THORAX',
    label: 'Thorax',
    icon: ICONS.ORANGE,
    survival_modifier: 0.75,
    immediate_interventions: ['Needle decompression', 'Chest seal'],
    surgical_capability: 'Thoracic surgery'
  },
  ABDOMEN: {
    code: 'ABDOMEN',
    label: 'Abdomen',
    icon: ICONS.ORANGE,
    survival_modifier: 0.70,
    surgical_capability: 'General surgery',
    notes: 'DCS critical - damage control laparotomy'
  },
  EXTREMITY: {
    code: 'EXTREMITY',
    label: 'Extremity',
    icon: ICONS.GREEN,
    survival_modifier: 0.92,
    immediate_interventions: ['Tourniquet', 'Hemostatic dressing'],
    notes: 'Most survivable with proper tourniquet use'
  },
  PELVIS: {
    code: 'PELVIS',
    label: 'Pelvis',
    icon: ICONS.ORANGE,
    survival_modifier: 0.72,
    immediate_interventions: ['Pelvic binder', 'TXA'],
    notes: 'Can lose 3L blood - pelvic binder essential'
  },
  MULTIPLE: {
    code: 'MULTIPLE',
    label: 'Multiple Regions',
    icon: ICONS.RED,
    survival_modifier: 0.55,
    notes: 'Survival decreases with each additional region',
    educational: {
      teaching_note: 'Artillery typically causes multiple region injuries'
    }
  }
};

// ============================================================================
// ENVIRONMENTAL SURVIVAL MODIFIERS
// ============================================================================

/**
 * Environmental factors affect survival
 */
export const ENVIRONMENTAL_SURVIVAL_MODIFIERS = {
  TEMPERATURE: {
    EXTREME_COLD: {
      code: 'EXTREME_COLD',
      label: 'Extreme Cold (<-20°C)',
      icon: ICONS.BLUE,
      survival_modifier: 0.75,
      notes: 'Hypothermia accelerates, frostbite risk'
    },
    COLD: {
      code: 'COLD',
      label: 'Cold (-20 to 0°C)',
      icon: ICONS.BLUE,
      survival_modifier: 0.85,
      notes: 'Hypothermia prevention critical'
    },
    TEMPERATE: {
      code: 'TEMPERATE',
      label: 'Temperate (0 to 25°C)',
      icon: ICONS.GREEN,
      survival_modifier: 1.0,
      notes: 'Baseline conditions'
    },
    HOT: {
      code: 'HOT',
      label: 'Hot (25 to 40°C)',
      icon: ICONS.ORANGE,
      survival_modifier: 0.90,
      notes: 'Heat illness risk, dehydration'
    },
    EXTREME_HOT: {
      code: 'EXTREME_HOT',
      label: 'Extreme Hot (>40°C)',
      icon: ICONS.RED,
      survival_modifier: 0.80,
      notes: 'Heat stroke risk, aggressive cooling needed'
    }
  },
  ALTITUDE: {
    SEA_LEVEL: {
      code: 'SEA_LEVEL',
      label: 'Sea Level (<1000m)',
      icon: ICONS.GREEN,
      survival_modifier: 1.0
    },
    MODERATE: {
      code: 'MODERATE',
      label: 'Moderate (1000-2500m)',
      icon: ICONS.GREEN,
      survival_modifier: 0.98
    },
    HIGH: {
      code: 'HIGH',
      label: 'High (2500-4000m)',
      icon: ICONS.YELLOW,
      survival_modifier: 0.92,
      notes: 'Altitude illness possible, helicopter performance degraded'
    },
    EXTREME: {
      code: 'EXTREME',
      label: 'Extreme (>4000m)',
      icon: ICONS.ORANGE,
      survival_modifier: 0.82,
      notes: 'Severe hypoxia, helicopter may not operate'
    }
  }
};

// ============================================================================
// EVACUATION MODE SURVIVAL
// ============================================================================

/**
 * Evacuation mode affects survival rate
 */
export const EVACUATION_MODE_SURVIVAL = {
  MEDEVAC_HELICOPTER: {
    code: 'MEDEVAC_HELICOPTER',
    label: 'MEDEVAC Helicopter',
    icon: ICONS.GREEN,
    survival_modifier: 1.0,
    en_route_care: 'FULL',
    time_advantage: 'Maximum'
  },
  CASEVAC_HELICOPTER: {
    code: 'CASEVAC_HELICOPTER',
    label: 'CASEVAC Helicopter',
    icon: ICONS.YELLOW,
    survival_modifier: 0.92,
    en_route_care: 'LIMITED',
    time_advantage: 'Maximum'
  },
  GROUND_AMBULANCE: {
    code: 'GROUND_AMBULANCE',
    label: 'Ground Ambulance',
    icon: ICONS.GREEN,
    survival_modifier: 0.95,
    en_route_care: 'FULL',
    time_advantage: 'Variable'
  },
  CASEVAC_GROUND: {
    code: 'CASEVAC_GROUND',
    label: 'CASEVAC Ground Vehicle',
    icon: ICONS.ORANGE,
    survival_modifier: 0.85,
    en_route_care: 'MINIMAL',
    time_advantage: 'Variable'
  },
  SELF_EVACUATION: {
    code: 'SELF_EVACUATION',
    label: 'Self/Buddy Evacuation',
    icon: ICONS.RED,
    survival_modifier: 0.70,
    en_route_care: 'NONE',
    time_advantage: 'None'
  }
};

// ============================================================================
// INTERVENTION TIMING EFFECTS
// ============================================================================

/**
 * Specific intervention timing affects survival
 */
export const INTERVENTION_TIMING_EFFECTS = {
  TOURNIQUET: {
    name: 'Tourniquet',
    optimal_window_minutes: 2,
    survival_by_timing: {
      under_2_min: 0.95,
      '2_to_5_min': 0.87,
      '5_to_10_min': 0.75,
      '10_to_15_min': 0.64,
      over_15_min: 0.45
    },
    educational: {
      teaching_note: 'Every minute counts - 95% survival at 2min vs 45% at 15min'
    }
  },
  TXA: {
    name: 'Tranexamic Acid',
    optimal_window_minutes: 60,
    harmful_after_minutes: 180,
    survival_by_timing: {
      under_60_min: 0.32,
      '60_to_120_min': 0.25,
      '120_to_180_min': 0.15,
      over_180_min: -0.08
    },
    educational: {
      teaching_note: 'TXA after 3 hours INCREASES mortality by 8%'
    }
  },
  NEEDLE_DECOMPRESSION: {
    name: 'Needle Decompression',
    optimal_window_minutes: 5,
    survival_by_timing: {
      under_3_min: 0.95,
      '3_to_5_min': 0.88,
      '5_to_8_min': 0.75,
      '8_to_10_min': 0.55,
      over_10_min: 0.30
    }
  },
  AIRWAY: {
    name: 'Airway Management',
    optimal_window_minutes: 3,
    survival_by_timing: {
      under_3_min: 0.95,
      '3_to_5_min': 0.80,
      '5_to_8_min': 0.50,
      over_8_min: 0.20
    }
  }
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get time window options for UI dropdown
 * @returns {Array} Time window options
 */
export const getTimeWindowOptions = () => {
  return Object.entries(SURVIVAL_TIME_WINDOWS).map(([key, window]) => ({
    value: key,
    label: window.label,
    icon: window.icon,
    color: window.color,
    survivalRate: window.t1_survival,
    rangeMinutes: window.range_minutes,
    pfcRequired: window.pfc_required || false
  }));
};

/**
 * Get triage category options for UI dropdown
 * @returns {Array} Triage category options
 */
export const getTriageCategoryOptions = () => {
  return [
    { value: 'T1', label: 'T1 - Immediate', icon: T1_SURVIVAL_CURVE.icon, color: T1_SURVIVAL_CURVE.color },
    { value: 'T2', label: 'T2 - Delayed', icon: T2_SURVIVAL_CURVE.icon, color: T2_SURVIVAL_CURVE.color },
    { value: 'T3', label: 'T3 - Minimal', icon: T3_SURVIVAL_CURVE.icon, color: T3_SURVIVAL_CURVE.color },
    { value: 'T4', label: 'T4 - Expectant', icon: T4_SURVIVAL_CURVE.icon, color: T4_SURVIVAL_CURVE.color }
  ];
};

/**
 * Get mechanism options for UI dropdown
 * @returns {Array} Mechanism options
 */
export const getMechanismOptions = () => {
  return Object.entries(MECHANISM_SURVIVAL_MODIFIERS).map(([key, mech]) => ({
    value: key,
    label: mech.label,
    icon: mech.icon,
    survivalModifier: mech.base_survival_modifier,
    ukrainePercentage: mech.ukraine_data?.percentage_2024
  }));
};

/**
 * Get body region options for UI dropdown
 * @returns {Array} Body region options
 */
export const getBodyRegionOptions = () => {
  return Object.entries(BODY_REGION_SURVIVAL_MODIFIERS).map(([key, region]) => ({
    value: key,
    label: region.label,
    icon: region.icon,
    survivalModifier: region.survival_modifier
  }));
};

/**
 * Get PFC level options for UI dropdown
 * @returns {Array} PFC level options
 */
export const getPFCLevelOptions = () => {
  return Object.entries(PFC_SURVIVAL_MODIFIERS).map(([key, pfc]) => ({
    value: key,
    label: pfc.label,
    icon: pfc.icon,
    modifier: pfc.modifier
  }));
};

/**
 * Get evacuation mode options for UI dropdown
 * @returns {Array} Evacuation mode options
 */
export const getEvacuationModeOptions = () => {
  return Object.entries(EVACUATION_MODE_SURVIVAL).map(([key, mode]) => ({
    value: key,
    label: mode.label,
    icon: mode.icon,
    survivalModifier: mode.survival_modifier,
    enRouteCare: mode.en_route_care
  }));
};

// ============================================================================
// CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate T1 survival rate by time (minutes)
 * @param {number} minutes - Time since injury
 * @returns {number} Survival rate
 */
export const calculateT1Survival = (minutes) => {
  const curve = T1_SURVIVAL_CURVE.survival_by_minutes;
  const times = Object.keys(curve).map(Number).sort((a, b) => a - b);
  
  if (minutes <= times[0]) return curve[times[0]];
  if (minutes >= times[times.length - 1]) return curve[times[times.length - 1]];
  
  // Linear interpolation
  for (let i = 0; i < times.length - 1; i++) {
    if (minutes >= times[i] && minutes < times[i + 1]) {
      const ratio = (minutes - times[i]) / (times[i + 1] - times[i]);
      return curve[times[i]] - ratio * (curve[times[i]] - curve[times[i + 1]]);
    }
  }
  
  return 0.15;
};

/**
 * Calculate T2 survival rate by time (hours)
 * @param {number} hours - Time since injury
 * @returns {number} Survival rate
 */
export const calculateT2Survival = (hours) => {
  const curve = T2_SURVIVAL_CURVE.survival_by_hours;
  const times = Object.keys(curve).map(Number).sort((a, b) => a - b);
  
  if (hours <= times[0]) return curve[times[0]];
  if (hours >= times[times.length - 1]) return curve[times[times.length - 1]];
  
  for (let i = 0; i < times.length - 1; i++) {
    if (hours >= times[i] && hours < times[i + 1]) {
      const ratio = (hours - times[i]) / (times[i + 1] - times[i]);
      return curve[times[i]] - ratio * (curve[times[i]] - curve[times[i + 1]]);
    }
  }
  
  return 0.82;
};

/**
 * Calculate T3 survival rate by time (hours)
 * @param {number} hours - Time since injury
 * @returns {number} Survival rate
 */
export const calculateT3Survival = (hours) => {
  const curve = T3_SURVIVAL_CURVE.survival_by_hours;
  const times = Object.keys(curve).map(Number).sort((a, b) => a - b);
  
  if (hours <= times[0]) return curve[times[0]];
  if (hours >= times[times.length - 1]) return curve[times[times.length - 1]];
  
  for (let i = 0; i < times.length - 1; i++) {
    if (hours >= times[i] && hours < times[i + 1]) {
      const ratio = (hours - times[i]) / (times[i + 1] - times[i]);
      return curve[times[i]] - ratio * (curve[times[i]] - curve[times[i + 1]]);
    }
  }
  
  return 0.97;
};

/**
 * Calculate Golden Hour Benefit - survival improvement from faster evacuation
 * 
 * This function calculates how much survival improves by achieving faster
 * evacuation times. Used for DOW (Died of Wounds) calculations and
 * educational demonstration of evacuation time importance.
 * 
 * @param {number} minutes - Actual evacuation time in minutes
 * @returns {Object} Golden hour benefit analysis
 * 
 * Ukraine 2022-2024 Data:
 * - Median time to Role 2: 85 minutes
 * - Golden hour achieved: 38% of evacuations
 * - Helicopter evac time: 35 min average
 * - Ground evac time: 120 min average
 * - Ground mortality increase: 85% higher than helicopter
 */
export const calculateGoldenHourBenefit = (minutes) => {
  // Calculate survival at actual time
  const actualSurvival = calculateT1Survival(minutes);
  
  // Calculate survival if golden hour was achieved
  const goldenHourSurvival = calculateT1Survival(60);
  
  // Calculate survival if platinum 10 was achieved
  const platinumSurvival = calculateT1Survival(10);
  
  // Determine time window category
  let timeWindow, icon, color, pfcRequired;
  
  if (minutes <= 10) {
    timeWindow = 'PLATINUM_10';
    icon = ICONS.GREEN;
    color = '#4CAF50';
    pfcRequired = false;
  } else if (minutes <= 60) {
    timeWindow = 'GOLDEN_HOUR';
    icon = ICONS.GREEN;
    color = '#66BB6A';
    pfcRequired = false;
  } else if (minutes <= 120) {
    timeWindow = 'EXTENDED';
    icon = ICONS.YELLOW;
    color = '#FFC107';
    pfcRequired = true;
  } else if (minutes <= 180) {
    timeWindow = 'PROLONGED';
    icon = ICONS.ORANGE;
    color = '#FF9800';
    pfcRequired = true;
  } else {
    timeWindow = 'CRITICAL';
    icon = ICONS.RED;
    color = '#F44336';
    pfcRequired = true;
  }
  
  // Calculate benefit metrics
  const survivalLostVsGoldenHour = goldenHourSurvival - actualSurvival;
  const survivalLostVsPlatinum = platinumSurvival - actualSurvival;
  const percentLostVsGoldenHour = Math.round((survivalLostVsGoldenHour / goldenHourSurvival) * 100);
  
  // Calculate preventable deaths per 100 T1 casualties
  const preventableDeathsPer100 = Math.round(survivalLostVsGoldenHour * 100);
  
  // Determine if golden hour was achieved
  const goldenHourAchieved = minutes <= 60;
  const platinumAchieved = minutes <= 10;
  
  return {
    // Input
    evacuation_time_minutes: minutes,
    
    // Survival rates
    actual_survival: Math.round(actualSurvival * 100) / 100,
    golden_hour_survival: goldenHourSurvival,
    platinum_survival: platinumSurvival,
    
    // Benefit analysis
    survival_lost_vs_golden_hour: Math.round(survivalLostVsGoldenHour * 100) / 100,
    survival_lost_vs_platinum: Math.round(survivalLostVsPlatinum * 100) / 100,
    percent_lost_vs_golden_hour: percentLostVsGoldenHour,
    preventable_deaths_per_100: preventableDeathsPer100,
    
    // Status
    golden_hour_achieved: goldenHourAchieved,
    platinum_achieved: platinumAchieved,
    time_window: timeWindow,
    pfc_required: pfcRequired,
    
    // Display
    icon,
    color,
    
    // Educational
    educational: {
      key_point: goldenHourAchieved 
        ? 'Golden hour achieved - optimal outcome expected'
        : `${preventableDeathsPer100} additional deaths per 100 T1 casualties due to delayed evacuation`,
      teaching_note: pfcRequired
        ? 'Prolonged Field Care skills essential at this evacuation time'
        : 'Standard TCCC interventions sufficient',
      ukraine_comparison: minutes <= 85 
        ? 'Better than Ukraine median (85 min)'
        : 'Worse than Ukraine median (85 min)'
    }
  };
};

// Restored 2026-10-09: isGoldenHourAchievable() expects threat conditions 'HIGH' / 'MEDIUM' / 'LOW';
// the scenario uses the THREAT_LEVELS keys. Map between them here.
export const TRANSPORT_THREAT_CONDITION = {
  LOW_THREAT: 'LOW',
  MODERATE_THREAT: 'MEDIUM',
  HIGH_THREAT: 'HIGH',
  EXTREME_THREAT: 'HIGH'
};

/**
 * Check if golden hour is achievable given distance and transport
 * @param {number} distanceKm - Distance to surgical care in km
 * @param {string} transportMode - 'HELICOPTER' or 'GROUND'
 * @param {Object} conditions - Optional conditions (weather, threat, etc.)
 * @returns {Object} Golden hour achievability analysis
 */
export const isGoldenHourAchievable = (distanceKm, transportMode = 'GROUND', conditions = {}) => {
  // Speed estimates (km/h)
  const speeds = {
    HELICOPTER: 200,  // Average helicopter MEDEVAC speed
    GROUND_PERMISSIVE: 60,  // Good roads, no threat
    GROUND_CONTESTED: 40,  // Some threat, degraded roads
    GROUND_DENIED: 25  // High threat, damaged roads
  };
  
  // Get appropriate speed
  let speed;
  if (transportMode === 'HELICOPTER') {
    speed = speeds.HELICOPTER * (conditions.weatherModifier || 1.0);
  } else {
    const groundType = conditions.threatLevel === 'HIGH' ? 'GROUND_DENIED' 
      : conditions.threatLevel === 'MEDIUM' ? 'GROUND_CONTESTED' 
      : 'GROUND_PERMISSIVE';
    speed = speeds[groundType];
  }
  
  // Calculate time components
  const notificationTime = conditions.notificationMinutes || 5;
  const launchTime = transportMode === 'HELICOPTER' ? 15 : 5;
  const loadTime = 5;
  const travelTime = (distanceKm / speed) * 60;  // Convert to minutes
  const totalTime = notificationTime + launchTime + loadTime + travelTime;
  
  // Determine achievability
  const achievable = totalTime <= 60;
  const marginMinutes = 60 - totalTime;
  
  return {
    distance_km: distanceKm,
    transport_mode: transportMode,
    estimated_time_minutes: Math.round(totalTime),
    achievable,
    margin_minutes: Math.round(marginMinutes),
    time_breakdown: {
      notification: notificationTime,
      launch: launchTime,
      load: loadTime,
      travel: Math.round(travelTime)
    },
    icon: achievable ? ICONS.GREEN : ICONS.RED,
    recommendation: achievable 
      ? 'Golden hour achievable with current setup'
      : transportMode === 'GROUND' 
        ? 'Consider helicopter MEDEVAC to achieve golden hour'
        : 'Forward surgical capability may be required'
  };
};

/**
 * Calculate survival with PFC adjustment
 * @param {number} minutes - Evacuation time in minutes
 * @param {string} pfcLevel - PFC level code
 * @returns {Object} Survival calculation with PFC
 */
export const calculateSurvivalWithPFC = (minutes, pfcLevel = 'NO_PFC') => {
  const baseSurvival = calculateT1Survival(minutes);
  const pfc = PFC_SURVIVAL_MODIFIERS[pfcLevel] || PFC_SURVIVAL_MODIFIERS.NO_PFC;
  
  if (minutes <= 60) {
    return {
      base_survival: baseSurvival,
      pfc_modifier: 1.0,
      final_survival: baseSurvival,
      pfc_applied: false,
      icon: ICONS.GREEN,
      notes: 'Within golden hour - PFC not required'
    };
  }
  
  const goldenHourSurvival = calculateT1Survival(60);
  const degradation = goldenHourSurvival - baseSurvival;
  const mitigatedDegradation = degradation * (1 - pfc.modifier);
  const adjustedSurvival = baseSurvival + mitigatedDegradation;
  
  return {
    base_survival: Math.round(baseSurvival * 100) / 100,
    pfc_modifier: pfc.modifier,
    final_survival: Math.round(Math.min(goldenHourSurvival, adjustedSurvival) * 100) / 100,
    pfc_applied: true,
    pfc_level: pfcLevel,
    icon: pfc.icon,
    notes: pfc.description
  };
};

/**
 * Calculate complete survival with all modifiers
 * @param {number} minutes - Evacuation time in minutes
 * @param {Object} modifiers - All applicable modifiers
 * @returns {Object} Complete survival calculation
 */
export const calculateSurvivalComplete = (minutes, modifiers = {}) => {
  const {
    triageCategory = 'T1',
    mechanism = 'ARTILLERY',
    bodyRegion = null,
    pfcLevel = 'NO_PFC',
    temperature = 'TEMPERATE',
    altitude = 'SEA_LEVEL',
    evacuationMode = 'MEDEVAC_HELICOPTER'
  } = modifiers;
  
  // Base survival by category
  let baseSurvival;
  if (triageCategory === 'T1') {
    baseSurvival = calculateT1Survival(minutes);
  } else if (triageCategory === 'T2') {
    baseSurvival = calculateT2Survival(minutes / 60);
  } else if (triageCategory === 'T3') {
    baseSurvival = calculateT3Survival(minutes / 60);
  } else {
    baseSurvival = 0.05;
  }
  
  // Apply modifiers
  const mechMod = MECHANISM_SURVIVAL_MODIFIERS[mechanism];
  const mechanismEffect = mechMod ? mechMod.base_survival_modifier : 1.0;
  
  const regionMod = bodyRegion ? BODY_REGION_SURVIVAL_MODIFIERS[bodyRegion] : null;
  const regionEffect = regionMod ? regionMod.survival_modifier : 1.0;
  
  const tempMod = ENVIRONMENTAL_SURVIVAL_MODIFIERS.TEMPERATURE[temperature];
  const tempEffect = tempMod ? tempMod.survival_modifier : 1.0;
  
  const altMod = ENVIRONMENTAL_SURVIVAL_MODIFIERS.ALTITUDE[altitude];
  const altEffect = altMod ? altMod.survival_modifier : 1.0;
  
  const evacMod = EVACUATION_MODE_SURVIVAL[evacuationMode];
  const evacEffect = evacMod ? evacMod.survival_modifier : 1.0;
  
  // Calculate modified survival
  let modifiedSurvival = baseSurvival * mechanismEffect * regionEffect * tempEffect * altEffect * evacEffect;
  
  // Apply PFC if T1 and beyond golden hour
  if (triageCategory === 'T1' && minutes > 60) {
    const pfcResult = calculateSurvivalWithPFC(minutes, pfcLevel);
    modifiedSurvival = pfcResult.final_survival * mechanismEffect * regionEffect * tempEffect * altEffect * evacEffect;
  }
  
  // Cap survival
  modifiedSurvival = Math.min(Math.max(modifiedSurvival, 0), 1);
  
  return {
    evacuation_time_minutes: minutes,
    triage_category: triageCategory,
    base_survival: Math.round(baseSurvival * 100) / 100,
    final_survival: Math.round(modifiedSurvival * 100) / 100,
    icon: modifiedSurvival >= 0.8 ? ICONS.GREEN : (modifiedSurvival >= 0.5 ? ICONS.YELLOW : ICONS.RED),
    
    modifiers_applied: {
      mechanism: { value: mechanism, effect: mechanismEffect, icon: mechMod?.icon },
      body_region: bodyRegion ? { value: bodyRegion, effect: regionEffect, icon: regionMod?.icon } : null,
      temperature: { value: temperature, effect: tempEffect, icon: tempMod?.icon },
      altitude: { value: altitude, effect: altEffect, icon: altMod?.icon },
      evacuation: { value: evacuationMode, effect: evacEffect, icon: evacMod?.icon },
      pfc: minutes > 60 ? { level: pfcLevel } : null
    },
    
    golden_hour_achieved: minutes <= 60,
    pfc_required: minutes > 60 && triageCategory === 'T1'
  };
};

/**
 * Get time window for given minutes
 * @param {number} minutes - Time in minutes
 * @returns {Object} Time window data
 */
export const getTimeWindow = (minutes) => {
  for (const [key, window] of Object.entries(SURVIVAL_TIME_WINDOWS)) {
    if (minutes >= window.range_minutes[0] && minutes < window.range_minutes[1]) {
      return { code: key, ...window };
    }
  }
  return { code: 'CATASTROPHIC', ...SURVIVAL_TIME_WINDOWS.CATASTROPHIC };
};

/**
 * Calculate intervention effectiveness by timing
 * @param {string} intervention - Intervention type
 * @param {number} minutes - Minutes since injury
 * @returns {Object} Intervention effectiveness
 */
export const getInterventionEffectiveness = (intervention, minutes) => {
  const intData = INTERVENTION_TIMING_EFFECTS[intervention];
  if (!intData) return null;
  
  const timings = intData.survival_by_timing;
  let effectiveness;
  let category;
  let icon;
  
  if (minutes <= 2) {
    effectiveness = timings.under_2_min || timings['under_3_min'] || timings['under_5_min'] || timings['under_60_min'];
    category = 'OPTIMAL';
    icon = ICONS.GREEN;
  } else if (minutes <= 5) {
    effectiveness = timings['2_to_5_min'] || timings['3_to_5_min'] || timings['under_5_min'] || timings['under_60_min'];
    category = 'GOOD';
    icon = ICONS.GREEN;
  } else if (minutes <= 10) {
    effectiveness = timings['5_to_10_min'] || timings['5_to_8_min'] || timings['under_60_min'];
    category = 'ACCEPTABLE';
    icon = ICONS.YELLOW;
  } else if (minutes <= 60) {
    effectiveness = timings['10_to_15_min'] || timings['under_60_min'] || 0.50;
    category = 'DEGRADED';
    icon = ICONS.ORANGE;
  } else {
    effectiveness = timings['over_15_min'] || timings['60_to_120_min'] || timings['over_180_min'] || 0.30;
    category = 'POOR';
    icon = ICONS.RED;
  }
  
  // Check for harmful timing (TXA > 3 hours)
  if (intervention === 'TXA' && minutes > 180 && intData.harmful_after_minutes) {
    effectiveness = -0.08;
    category = 'HARMFUL';
    icon = ICONS.RED;
  }
  
  return {
    intervention,
    time_since_injury: minutes,
    optimal_window: intData.optimal_window_minutes,
    effectiveness,
    effectiveness_category: category,
    icon,
    within_optimal: minutes <= intData.optimal_window_minutes,
    harmful: effectiveness < 0
  };
};

// ============================================================================
// MONTE CARLO FUNCTIONS
// ============================================================================

/**
 * Generate random evacuation time for Monte Carlo
 * @param {Object} paradigm - Evacuation paradigm
 * @returns {number} Random time in minutes
 */
export const getRandomEvacuationTime = (paradigm) => {
  const dist = paradigm?.evacuation_time_distribution;
  if (!dist) {
    const rand = Math.random();
    if (rand < 0.08) return 15 + Math.random() * 15;
    if (rand < 0.25) return 30 + Math.random() * 30;
    if (rand < 0.60) return 60 + Math.random() * 60;
    if (rand < 0.88) return 120 + Math.random() * 60;
    return 180 + Math.random() * 60;
  }
  
  const rand = Math.random();
  let cumulative = 0;
  
  cumulative += dist.under_30min || 0;
  if (rand < cumulative) return 15 + Math.random() * 15;
  
  cumulative += dist['30_to_60min'] || 0;
  if (rand < cumulative) return 30 + Math.random() * 30;
  
  cumulative += dist['60_to_120min'] || 0;
  if (rand < cumulative) return 60 + Math.random() * 60;
  
  cumulative += dist['120_to_180min'] || 0;
  if (rand < cumulative) return 120 + Math.random() * 60;
  
  return 180 + Math.random() * 60;
};

/**
 * Run Monte Carlo survival simulation
 * @param {number} casualties - Number of T1 casualties
 * @param {Object} paradigm - Evacuation paradigm
 * @param {number} iterations - Number of iterations
 * @returns {Object} Monte Carlo results
 */
export const runSurvivalMonteCarlo = (casualties, paradigm, iterations = 1000) => {
  const results = {
    survivors: 0,
    died: 0,
    golden_hour_achieved: 0,
    pfc_required: 0,
    avg_time: 0
  };
  
  let totalTime = 0;
  
  for (let i = 0; i < iterations; i++) {
    const time = getRandomEvacuationTime(paradigm);
    const survival = calculateT1Survival(time);
    
    totalTime += time;
    
    if (time <= 60) results.golden_hour_achieved++;
    if (time > 60) results.pfc_required++;
    
    for (let c = 0; c < casualties; c++) {
      if (Math.random() < survival) {
        results.survivors++;
      } else {
        results.died++;
      }
    }
  }
  
  const totalCasualties = casualties * iterations;
  
  return {
    total_casualties: totalCasualties,
    survivors: results.survivors,
    died: results.died,
    survival_rate: Math.round((results.survivors / totalCasualties) * 100) / 100,
    golden_hour_rate: Math.round((results.golden_hour_achieved / iterations) * 100) / 100,
    pfc_rate: Math.round((results.pfc_required / iterations) * 100) / 100,
    avg_evacuation_time: Math.round(totalTime / iterations),
    iterations,
    icon: results.survivors / totalCasualties >= 0.8 ? ICONS.GREEN : ICONS.YELLOW
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate survival configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateSurvivalConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  // Validate triage category
  if (config.triageCategory && !['T1', 'T2', 'T3', 'T4'].includes(config.triageCategory)) {
    errors.push({
      field: 'triageCategory',
      message: `Invalid triage category: ${config.triageCategory}`,
      icon: ICONS.RED
    });
  }
  
  // Validate mechanism
  if (config.mechanism && !MECHANISM_SURVIVAL_MODIFIERS[config.mechanism]) {
    errors.push({
      field: 'mechanism',
      message: `Invalid mechanism: ${config.mechanism}`,
      icon: ICONS.RED
    });
  }
  
  // Validate PFC level
  if (config.pfcLevel && !PFC_SURVIVAL_MODIFIERS[config.pfcLevel]) {
    errors.push({
      field: 'pfcLevel',
      message: `Invalid PFC level: ${config.pfcLevel}`,
      icon: ICONS.RED
    });
  }
  
  // Check T1 with extended time but no PFC
  if (config.triageCategory === 'T1' && config.evacuationTime > 60 && config.pfcLevel === 'NO_PFC') {
    warnings.push({
      field: 'pfcLevel',
      message: 'T1 casualty with >60min evacuation requires PFC',
      icon: ICONS.WARNING
    });
  }
  
  // Check TXA timing
  if (config.txaTime && config.txaTime > 180) {
    errors.push({
      field: 'txaTime',
      message: 'TXA after 3 hours is HARMFUL - do not administer',
      icon: ICONS.RED
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if triage category is valid
 * @param {string} category - Category to check
 * @returns {boolean} True if valid
 */
export const isValidTriageCategory = (category) => {
  return ['T1', 'T2', 'T3', 'T4'].includes(category);
};

/**
 * Get all valid triage categories
 * @returns {Array} Valid categories
 */
export const getValidTriageCategories = () => ['T1', 'T2', 'T3', 'T4'];

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const survivalCurvesConstants = {
  // Time windows
  SURVIVAL_TIME_WINDOWS,
  
  // Survival curves by category
  T1_SURVIVAL_CURVE,
  T2_SURVIVAL_CURVE,
  T3_SURVIVAL_CURVE,
  T4_SURVIVAL_CURVE,
  
  // Modifiers
  PFC_SURVIVAL_MODIFIERS,
  MECHANISM_SURVIVAL_MODIFIERS,
  BODY_REGION_SURVIVAL_MODIFIERS,
  ENVIRONMENTAL_SURVIVAL_MODIFIERS,
  EVACUATION_MODE_SURVIVAL,
  INTERVENTION_TIMING_EFFECTS,
  
  // UI Options
  getTimeWindowOptions,
  getTriageCategoryOptions,
  getMechanismOptions,
  getBodyRegionOptions,
  getPFCLevelOptions,
  getEvacuationModeOptions,
  
  // Calculations
  calculateT1Survival,
  calculateT2Survival,
  calculateT3Survival,
  calculateGoldenHourBenefit,
  calculateSurvivalWithPFC,
  calculateSurvivalComplete,
  getTimeWindow,
  isGoldenHourAchievable,
  getInterventionEffectiveness,
  
  // Monte Carlo
  getRandomEvacuationTime,
  runSurvivalMonteCarlo,
  
  // Validation
  validateSurvivalConfig,
  isValidTriageCategory,
  getValidTriageCategories
};

export default survivalCurvesConstants;
